import { Button, Icon, Tooltip } from '@blueprintjs/core';
import AddressDetailTable from 'Components/Generic/Address/AddressDetailTable';
import {
  showSaveSuccessToast,
  showToast,
} from 'Components/Generic/BOSToasterUtil';
import DuplicateButton from 'Components/Generic/Buttons/DuplicateButton';
import FloatingActionMenu from 'Components/Generic/Buttons/FloatingActionMenu';
import DetailPanel from 'Components/Generic/DetailPanel';
import ErrorMessage from 'Components/Generic/Misc/ErrorMessage';
import NotesListPanel from 'Components/Generic/Notes/NotesListPanel';
import { SITE_PAGES } from 'Config';
import AppData from 'Data/AppData';
import ChargeAllocationData from 'Data/ChargeAllocationData';
import { ShippingStateProvider } from 'Data/context/Shipping';
import { useViewportSizer } from 'Data/context/ViewportSizer';
import { user_roles } from 'Data/Enum';
import { GetTransactionDataBus, TransactionData } from 'Data/TransactionData';
import DataManager from 'Lib/DataManager';
import { useStateWithRef } from 'Lib/hooks/useStateWithRef';
import VendorInformationPanel from 'Pages/Admin/Vendors/VendorInformationPanel';
import LineItemsPanel from 'Pages/Requests/RequestDetails/LineItemsPanel';
import RequestInformationPanel from 'Pages/Requests/RequestDetails/RequestInformationPanel';
import ChartfieldSetupPanel from 'Pages/Transactions/ChartfieldSetupPanel';
import TransactionDocumentsPanel from 'Pages/Transactions/TransactionDetails/TransactionDocumentsPanel';
import TransactionInformationPanel from 'Pages/Transactions/TransactionDetails/TransactionInformationPanel';
import TransactionLanding from 'Pages/Transactions/TransactionLanding';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import styles from 'Styles/styles.module.css';
import { isUserAuthorized } from 'Util';
import { generatePDFforTransaction } from 'Util/pdfUtil/transactionPrintHelper';
import { LINE_ITEM_STATUS, MISMATCHED_CORE_WARNING_TEXT } from '../Enum';

let DM = new DataManager();

const TransactionDetails = ({
  _isNew,
  transactionObject,
  parentBreadcrumb,
  params,
  transactionId,
  showEssentialsOnly = false,
  onTransactionUpdated,
  editable = true,
}) => {
  const { narrowScreen } = useViewportSizer();
  const location = useLocation();
  const navigate = useNavigate();

  const fromReconcile = location?.state?.fromReconcile;
  const fromReview = location?.state?.fromReview;
  const TransactionDataRef = useRef(GetTransactionDataBus());
  let CurrentTransactionData = TransactionDataRef.current;

  const [transaction, setTransaction, transRef] = useStateWithRef(
    transactionObject
      ? TransactionData.getTransactionDetails(transactionObject, true)
      : undefined,
  );
  const [isNew, setIsNew, isNewRef] = useStateWithRef(false);
  const [isExporting, setIsExporting] = useState(false);

  let transactionIsEditable =
    editable &&
    (isUserAuthorized({
      roles: [
        user_roles.ADMINISTRATOR,
        user_roles.BUYER,
        user_roles.TRANSACTOR,
      ],
    }) ||
      transaction?.created_by === AppData.userID() ||
      transaction?.purchased_by === AppData.userID());

  let breadcrumb = [
    SITE_PAGES.TRANSACTION_LANDING,
    ...(fromReconcile
      ? [SITE_PAGES.RECONCILE]
      : fromReview
      ? [SITE_PAGES.TRANS_REVIEW]
      : [SITE_PAGES.ALL_TRANSACTIONS]),
    {
      icon: 'document',
      text: params?.id || (isNew && 'New'),
    },
  ];

  useEffect(() => {
    if (parentBreadcrumb) {
      breadcrumb = parentBreadcrumb;
    }
  }, []);

  useEffect(() => {
    DM.add(
      CurrentTransactionData.registerListener(
        'RECORD_LOADED',
        onTransactionLoaded,
      ),
      CurrentTransactionData,
    );
    DM.add(
      TransactionData.registerListener(
        'BLANK_RECORD_LOADED',
        onBlankTransactionLoaded,
      ),
      TransactionData,
    );
    DM.add(
      TransactionData.registerListener(
        'TRANSACTION_CREATED',
        onTransactionCreated,
      ),
      TransactionData,
    );
    DM.add(
      TransactionData.registerListener(
        'TRANSACTION_CREATED_FROM_REQUEST',
        onTransactionCreated,
      ),
      TransactionData,
    );
    DM.add(
      TransactionData.registerListener(
        'PURCHASE_TOTAL_UPDATED',
        refreshTransaction,
      ),
      TransactionData,
    );
    DM.add(
      TransactionData.registerListener(
        'TRANSACTION_MARKED_AS_PURCHASED',
        refreshTransaction,
      ),
      TransactionData,
    );
    DM.add(
      CurrentTransactionData.registerListener(
        'RECORD_UPDATED',
        transactionUpdated,
      ),
      CurrentTransactionData,
    );
    DM.add(
      ChargeAllocationData.registerListener(
        'CHARGES_SAVED_FOR_TRANSACTION',
        onChargeAllocationsUpdated,
      ),
      ChargeAllocationData,
    );
    return () => {
      DM.clear();
    };
  }, []);

  useEffect(() => {
    transactionId &&
      transaction?.id !== transactionId &&
      CurrentTransactionData.getTransaction(transactionId);
  }, [transactionId]);

  useEffect(() => {
    params?.id &&
      transaction?.id !== params.id &&
      CurrentTransactionData.getTransaction(params.id);
    if (params?.id === 'null') {
      setIsNew(true);
    }
  }, [params]);

  useEffect(() => {
    if (_isNew) {
      setIsNew(true);
      !transactionObject && TransactionData.getBlank();
    }
  }, [_isNew]);

  const refreshTransaction = () => {
    transRef.current.id &&
      CurrentTransactionData.getTransaction(transRef.current.id);
  };

  const onTransactionLoaded = (payload) => {
    if (payload) {
      setTransaction(payload);
    }
  };

  const onBlankTransactionLoaded = (blankTransaction) => {
    if (blankTransaction) {
      setTransaction({
        ...blankTransaction,
        purchased_by: AppData.userID(),
        created_by: AppData.userID(),
      });
    }
  };

  const onTransactionCreated = (payload) => {
    if (payload) {
      showSaveSuccessToast('Transaction Created');
      CurrentTransactionData.getTransaction(payload.id);
    }
  };

  const onLineItemsSaved = () => {
    onTransactionUpdated && onTransactionUpdated();
    !onTransactionUpdated && refreshTransaction();
  };

  const onChargeAllocationsUpdated = (payload) => {
    if (
      payload?.length &&
      payload[0].parent_transaction_id === transRef.current.id
    ) {
      if (isNewRef.current) {
        navigate(`${SITE_PAGES.TRANSACTION_DETAIL.to}/${transRef.current.id}`);
      } else {
        refreshTransaction();
      }
    }
  };

  const transactionUpdated = (savedTrans) => {
    if (savedTrans.id === transRef.current.id) {
      CurrentTransactionData.getTransaction(savedTrans.id);
    }
  };

  let transactionCanBePurchased = !!transaction?.charge_allocations?.length;

  let confirmPurchaseButton = (
    <Tooltip
      content={
        !transactionCanBePurchased
          ? 'Charge allocations must be set up'
          : `Confirm purchase of transaction ${transaction?.id}`
      }>
      <Button
        className={styles.confirm_purchase_button}
        large
        disabled={!transactionCanBePurchased}
        onClick={() => TransactionData.markAsPurchased(transaction)}>
        <div
          style={{
            display: 'flex',
            gap: 6,
            justifyContent: 'center',
          }}>
          <Icon
            className={styles.confirm_purchase_button_icon}
            icon={'shopping-cart'}
          />
          Confirm Purchase
        </div>
      </Button>
    </Tooltip>
  );

  let statusButton = transaction?.line_items?.some(
    (line) =>
      line.status === LINE_ITEM_STATUS.OPEN.key ||
      line.status === LINE_ITEM_STATUS.PENDING.key,
  ) &&
    isUserAuthorized({
      roles: [
        user_roles.BUYER,
        user_roles.ADMINISTRATOR,
        user_roles.TRANSACTOR,
      ],
    }) && (
      <FloatingActionMenu>
        <FloatingActionMenu.MainButton>
          {confirmPurchaseButton}
        </FloatingActionMenu.MainButton>
      </FloatingActionMenu>
    );

  let noteIdList = transaction?.transaction_notes?.map((note) => note.id) || [];

  let pageContent = transaction && (
    <ShippingStateProvider
      transaction={transaction}
      editable={transactionIsEditable}>
      <div className={styles.panel_flex_box}>
        <TransactionInformationPanel
          TransactionDataBus={CurrentTransactionData}
          transactionObject={transaction}
          editable={transactionIsEditable}
          isNew={isNew}
          showTransactionId={showEssentialsOnly}
          hideDeliveryAddress={showEssentialsOnly}
        />
        {!showEssentialsOnly && transaction.request && (
          <RequestInformationPanel
            requestObject={transaction.request}
            title='Request Information'
            excludeIsRadioactive
            showRequestId
          />
        )}
        {!showEssentialsOnly && !isNew && (
          <VendorInformationPanel
            vendorObject={transaction.vendor}
            collapsible={false}
          />
        )}
        {!showEssentialsOnly &&
          !isNew &&
          transaction?.shippings?.length > 0 &&
          transaction?.shippings[0].delivery_address_id > 0 && (
            <DetailPanel title='Delivery Instructions'>
              <AddressDetailTable
                addressId={transaction?.shippings[0].delivery_address_id}
              />
            </DetailPanel>
          )}
        {
          <TransactionDocumentsPanel
            transaction={transaction}
            onDocumentsUpdated={!isNew ? refreshTransaction : undefined}
            disabled={isNew && !transaction.id}
            canAddDoc={!!transaction.id}
            editable={
              transaction?.documents?.length > 0 &&
              isUserAuthorized({
                roles: [
                  user_roles.ADMINISTRATOR,
                  user_roles.BUYER,
                  user_roles.TRANSACTOR,
                ],
              })
            }
          />
        }
        {
          <NotesListPanel
            notesIdList={noteIdList}
            notesTableName='transaction_notes'
            dataIdentifier='T_NOTES'
            disabled={isNew && !transaction.id}
            canAddNotes={!!transaction.id}
            editable={transactionIsEditable}
            hideToggle
            parentEditState={transactionIsEditable || !!transaction.id}
            newNoteFieldValues={{ transaction_id: transaction.id }}
            onNotesUpdated={refreshTransaction}
            collapsible={false}
          />
        }
        {transaction.category !== 'REVENUE' && (
          <div
            className={
              showEssentialsOnly
                ? styles.semi_wide_panel
                : styles.full_width_panel
            }>
            <LineItemsPanel
              transaction={transaction}
              editable={transactionIsEditable}
              disabled={isNew && !transaction.id}
              isNew={isNew}
              compact={showEssentialsOnly}
              onUpdateLineItems={onLineItemsSaved}
            />
          </div>
        )}
        {(!isNew ||
          transaction?.line_items?.length > 0 ||
          transaction.category === 'REVENUE') && (
          <div
            className={
              showEssentialsOnly
                ? styles.semi_wide_panel
                : styles.full_width_panel
            }>
            <ChartfieldSetupPanel
              transaction={transaction}
              lineItems={transaction.line_items}
              editable={transactionIsEditable}
              disabled={isNew && !transaction.id}
              isNew={isNew}
              compact={showEssentialsOnly}
            />
          </div>
        )}
        <div className={styles.full_width_panel} />
        {statusButton}
      </div>
    </ShippingStateProvider>
  );

  const exportPdfClicked = (download = false) => {
    if (!transaction?.id) {
      return;
    }
    setIsExporting(true);
    let callback = () => setIsExporting(false);
    let errorhandler = (e) => {
      console.error(`Error generating PDF for transaction: ${transaction.id}`);
      console.error(e);
      setIsExporting(false);
      showToast(`Error generating PDF: ${e.message}`, 'warning', 'issue');
    };

    generatePDFforTransaction(transaction, download, callback, errorhandler);
  };

  let openReportButton = transaction?.id && (
    <Tooltip
      minimal
      content={'Click to open transaction as PDF'}
      placement={'auto'}
      shouldReturnFocusOnClose={false}>
      <Button
        minimal
        className={styles.primary_button_minimal}
        onClick={() => exportPdfClicked(false)}
        icon={'document-open'}
        disabled={isExporting}>
        {narrowScreen ? '' : 'Open as PDF'}
      </Button>
    </Tooltip>
  );

  const duplicateClickHandler = () => {
    navigate(SITE_PAGES.NEW_TRANSACTION.to, { state: { transaction } });
  };

  let duplicateButton = !isNew &&
    transaction?.id &&
    isUserAuthorized({
      roles: [
        user_roles.ADMINISTRATOR,
        user_roles.BUYER,
        user_roles.TRANSACTOR,
      ],
    }) && (
      <DuplicateButton
        duplicateClickHandler={duplicateClickHandler}
        buttonText={narrowScreen ? '' : 'Duplicate Transaction'}
      />
    );

  let transactionAccountWarning =
    transaction?.charge_allocations?.length &&
    transaction.charge_allocations.some(
      (ca) =>
        ca.charge_account?.parent_organization_id !==
        transaction.parent_organization_id,
    );

  let titleText = isNew
    ? 'New Transaction'
    : `Transaction ${transaction?.id || params?.id}`;

  let title = transactionAccountWarning ? (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 8,
      }}>
      <span style={{ whiteSpace: 'nowrap' }}>{titleText}</span>
      <ErrorMessage
        small
        message={MISMATCHED_CORE_WARNING_TEXT}
        invertedColors
      />
    </div>
  ) : (
    titleText
  );

  let titleButtons = (
    <>
      {openReportButton}
      {duplicateButton}
    </>
  );

  return showEssentialsOnly ? (
    <div>{pageContent}</div>
  ) : (
    <TransactionLanding
      breadcrumbs={breadcrumb}
      title={title}
      titleButton={titleButtons}>
      {pageContent}
    </TransactionLanding>
  );
};

TransactionDetails.propTypes = {
  params: PropTypes.shape({
    id: PropTypes.string,
  }),
  transactionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  _isNew: PropTypes.bool,
  transactionObject: PropTypes.object,
  parentBreadcrumb: PropTypes.object,
  showEssentialsOnly: PropTypes.bool,
  onTransactionUpdated: PropTypes.func,
  editable: PropTypes.bool,
};

export default TransactionDetails;
